import { useEffect } from 'react';
import { useSearchParams, Routes, Route } from 'react-router-dom';
import { dismissAppLaunchSplash } from '@/lib/pwa/app-launch';
import { ClerkLoaded, ClerkLoading, SignUp } from '@clerk/react';
import { authWidgetAppearance } from '@/lib/auth/clerk-appearance';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import { buildSignupRedirectUrl, safeLocalPath } from '@/lib/auth/onboarding-intent';
import { parseEmailInviteIdFromNext } from '@/lib/auth/email-invite-login';
import { isDevAuthBypassEnabled } from '@/lib/dev/auth-bypass-flag';
import { AUTH_WIDGET_MIN_HEIGHT, AuthWidgetSkeleton } from '@/components/auth/AuthWidgetSkeleton';
import { AuthClerkTicketEffect } from '@/components/auth/AuthClerkTicketEffect';
import { AuthPageShell } from '@/components/auth/AuthPageShell';
import { DevBypassAuthPlaceholder } from '@/components/auth/DevBypassAuthPlaceholder';
import { LoginSessionGate } from '@/components/auth/LoginSessionGate';
import { LoginSsoCallback } from '@/components/auth/LoginSsoCallback';
import { RoamAppProviders } from '@/components/roam-app/RoamAppProviders';
import { useQuery } from '@/lib/data/convex-bypass';

function hasConfiguredEnv(value: string | undefined): value is string {
  const trimmed = value?.trim();
  return Boolean(trimmed && !trimmed.includes('...') && !trimmed.includes('<'));
}

export function LoginPage() {
  return (
    <Routes>
      <Route path="continue" element={<LoginPageContent completingSignUp />} />
      <Route path="sso-callback" element={<LoginSsoCallback />} />
      <Route path="*" element={<LoginPageContent />} />
    </Routes>
  );
}

function LoginPageContent({ completingSignUp = false }: { completingSignUp?: boolean }) {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    dismissAppLaunchSplash();
  }, []);
  const next = searchParams.get('next') ?? undefined;
  const signInRedirectUrl = safeLocalPath(next, '/trips/active');
  const signUpRedirectUrl = buildSignupRedirectUrl({ next });
  const authNext = next ? `?next=${encodeURIComponent(next)}` : '';
  const emailInviteId = parseEmailInviteIdFromNext(next);

  return (
    <AuthPageShell>
      {isDevAuthBypassEnabled ? (
        <DevBypassAuthPlaceholder />
      ) : (
        <div className={AUTH_WIDGET_MIN_HEIGHT['sign-in']}>
          <ClerkLoading>
            <AuthWidgetSkeleton variant="sign-in" />
          </ClerkLoading>
          <ClerkLoaded>
            {completingSignUp ? (
              <SignUp
                routing="hash"
                appearance={authWidgetAppearance}
                forceRedirectUrl={signUpRedirectUrl}
                signInUrl={`/login${authNext}`}
              />
            ) : (
              <LoginClerkLoaded
                signInRedirectUrl={signInRedirectUrl}
                signUpRedirectUrl={signUpRedirectUrl}
                authNextQuery={authNext}
                emailInviteId={emailInviteId}
              />
            )}
          </ClerkLoaded>
        </div>
      )}
    </AuthPageShell>
  );
}

function LoginClerkLoaded({
  signInRedirectUrl,
  signUpRedirectUrl,
  authNextQuery,
  emailInviteId,
}: {
  signInRedirectUrl: string;
  signUpRedirectUrl: string;
  authNextQuery: string;
  emailInviteId: string | null;
}) {
  const convexUrl = process.env.NEXT_PUBLIC_CONVEX_URL;
  const gate = (
    <>
      <AuthClerkTicketEffect
        signInRedirectUrl={signInRedirectUrl}
        signUpRedirectUrl={signUpRedirectUrl}
      />
      <LoginSessionGateWithInvite
        signInRedirectUrl={signInRedirectUrl}
        signUpRedirectUrl={signUpRedirectUrl}
        authNextQuery={authNextQuery}
        emailInviteId={emailInviteId}
      />
    </>
  );

  if (!hasConfiguredEnv(convexUrl)) {
    return gate;
  }

  return <RoamAppProviders convexUrl={convexUrl}>{gate}</RoamAppProviders>;
}

function LoginSessionGateWithInvite({
  signInRedirectUrl,
  signUpRedirectUrl,
  authNextQuery,
  emailInviteId,
}: {
  signInRedirectUrl: string;
  signUpRedirectUrl: string;
  authNextQuery: string;
  emailInviteId: string | null;
}) {
  const inviteHint = useQuery(
    api.tripSharing.getEmailInviteSignInHint,
    emailInviteId ? { tripInviteId: emailInviteId as Id<'tripInvites'> } : 'skip',
  );

  const lockedInviteEmail =
    emailInviteId && inviteHint !== undefined && inviteHint !== null ? inviteHint.email : null;
  const inviteTripTitle =
    emailInviteId && inviteHint !== undefined && inviteHint !== null ? inviteHint.tripTitle : null;
  const autoSendInviteCode = Boolean(lockedInviteEmail);

  return (
    <LoginSessionGate
      signInRedirectUrl={signInRedirectUrl}
      signUpRedirectUrl={signUpRedirectUrl}
      authNextQuery={authNextQuery}
      lockedInviteEmail={lockedInviteEmail}
      inviteTripTitle={inviteTripTitle}
      autoSendInviteCode={autoSendInviteCode}
    />
  );
}
